/*
 * Кнопка — действие, у которого есть вес.
 *
 * Пять «кнопок» кита собираются двумя осями классов: variant (громкость заливки) ×
 * tone (последствие действия). Разбор всех 126 текстовых кнопок — в
 * docs/ui-kit/contracts/button.md. Итоговые цвета складываются локальными переменными
 * --btn-*, справа от каждой стоит токен, а не значение.
 *
 * Порядок правил: базовое → размер → вариант → тон → части → invalid → focus-visible →
 * loading → disabled → hover → active. Ховер и нажатие стоят последними и закрыты
 * :not(:disabled):not(.loading) — так они не спорят с выключенностью и загрузкой ни
 * специфичностью, ни источником, а специфичность по файлу только растёт.
 *
 * Материал. Стекло несёт ровно один вариант — secondary: кит рисует его капсулой
 * rgba(255,255,255,0.7) с blur(12px) saturate(170%) (UI Kit 211), и без размытия она читается
 * наклейкой поверх стекла панели, а не его частью. Остальные три материалом не являются:
 * primary — сплошная тональная заливка, tonal — краска (акцентный тинт), ghost — вообще
 * никакой подложки (UI Kit 237: background:transparent). Размывать их нечем и незачем:
 * у прозрачной капсулы backdrop-filter рисует размытый прямоугольник на пустом месте,
 * а saturate поверх тинта съедает контраст той самой краски, ради которой тинт положен.
 * Разбор с цифрами — docs/ui-kit/contracts/button.md, раздел «Материал».
 *
 * Кнопка на стеклянной полосе (слот FloatingActionBar, TableToolbar, ряд действий FilterPanel,
 * AppTopBar) своего размытия не несёт: полоса объявляет --control-blur: none, наследование
 * доносит его до слота. Заливка, hairline и тень при этом остаются — кит рисует именно так
 * (Data Grid 274, Forms 228: rgba(255,255,255,0.7) без backdrop-filter). Механизм описан
 * в tokens.css рядом с лестницей --blur-*.
 *
 * Медиазапросов по ширине нет: кнопка одинакова на 360px и на десктопе, плотность
 * выбирается пропом size. Перенос ряда кнопок — забота контейнера.
 */

.root {
  --btn-h: var(--control-h-md);
  --btn-px: var(--space-5);
  --btn-font: var(--font-size-lg);
  --btn-bg: var(--color-surface);
  --btn-fg: var(--color-text);
  --btn-border: var(--color-border-strong);
  --btn-shadow: var(--shadow-control);
  --btn-ring: 0 0 0 0 transparent;
  --btn-ring-focus: var(--ring-focus);

  /* Размывает только стеклянный вариант, остальные оставляют none — см. шапку файла */
  --btn-blur: none;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;

  /*
   * min-height, а не height: длинная подпись обязана растить кнопку, а не обрезаться.
   * --btn-py в корне не объявлен: его ставит потребитель с двухстрочной подписью (аккаунт
   * в шапке), чтобы остаться в высоте контрола. Переменная, а не padding в его классе:
   * так исход не зависит от порядка, в котором собраны CSS-модули.
   */
  min-height: var(--btn-h);
  padding: var(--btn-py, var(--space-15)) var(--btn-px);
  color: var(--btn-fg);
  font-family: inherit;
  font-size: var(--btn-font);
  font-weight: var(--font-weight-semibold);
  line-height: var(--line-height-text);
  letter-spacing: var(--tracking-control);
  text-align: center;
  vertical-align: middle;
  cursor: pointer;
  background: var(--btn-bg);
  border: var(--border-hairline) solid var(--btn-border);
  border-radius: var(--radius-pill);
  box-shadow: var(--btn-ring), var(--btn-shadow);

  /* -webkit- обязателен рядом: планка Safari 15.4, см. ARCHITECTURE.md §19 */
  -webkit-backdrop-filter: var(--btn-blur);
  backdrop-filter: var(--btn-blur);
  transition:
    color var(--transition-fast),
    border-color var(--transition-fast),
    box-shadow var(--transition-fast),
    transform var(--transition-fast);
}

/* Шкала: кит даёт 26…52, решение Q4 сводит их к трём ступеням — тем же, что у Input */

.sm {
  --btn-h: var(--control-h-sm);
  --btn-px: var(--space-4);
  --btn-font: var(--font-size-sm);
}

.md {
  --btn-h: var(--control-h-md);
  --btn-px: var(--space-5);
  --btn-font: var(--font-size-lg);
}

.lg {
  --btn-h: var(--control-h-lg);
  --btn-px: var(--space-6);
  --btn-font: var(--font-size-xl);
}

/* Вариант и тон покоя */

.primary {
  --btn-bg: var(--gradient-accent);
  --btn-fg: var(--color-accent-text-on-solid);
  --btn-border: var(--color-accent-edge);
  --btn-shadow: var(--shadow-accent);
}

/*
 * Единственное стекло среди вариантов. Кит (UI Kit 211) даёт все четыре слагаемых материала:
 * заливку 0.7 белого, hairline по туши, тень с верхним бликом и blur(12px) saturate(170%).
 * --gloss-rim добавлен сверх кита: он обводит капсулу целиком, и без него в тёмной теме
 * (7 % белого) у стекла не видно толщины — верхнего блика 12 % там не хватает.
 */
.secondary {
  /* На стеклянной полосе размытие гасится её --control-blur, вне полосы работает запасное */
  --btn-blur: var(--control-blur, var(--blur-control));
  --btn-bg: var(--glass-control);
  --btn-fg: var(--color-text);
  --btn-border: var(--color-border-strong);
  --btn-shadow: var(--gloss-rim), var(--shadow-control);
}

.tonal {
  --btn-bg: var(--color-accent-tint);
  --btn-fg: var(--color-accent-text);
  --btn-border: var(--color-accent-border);
  --btn-shadow: 0 0 0 0 transparent;
}

.ghost {
  --btn-bg: transparent;
  --btn-fg: var(--color-accent-text);
  --btn-border: transparent;
  --btn-shadow: 0 0 0 0 transparent;
}

/*
 * Без фона подпись должна читаться как действие в строке, а не как кнопка:
 * кит рисует ghost с отступом на ступень меньше залитых вариантов.
 */

.ghost.sm {
  --btn-px: var(--space-3);
}

.ghost.md {
  --btn-px: var(--space-35);
}

.ghost.lg {
  --btn-px: var(--space-4);
}

/* Кольцо фокуса и halo ошибки у красной кнопки красные: синий на красной заливке чужой */
.toneDanger {
  --btn-ring-focus: var(--ring-error);
}

/*
 * Цветного «подъёма» под красной заливкой в системе нет (у акцента это --shadow-accent):
 * берётся только верхний блик — ровно тот inset, что нарисован в ките.
 */
.primary.toneDanger {
  --btn-bg: var(--gradient-danger);
  --btn-fg: var(--color-danger-text-on-solid);
  --btn-border: var(--color-danger-edge);
  --btn-shadow: var(--gloss-top-on-accent);
}

/* Стекло с красной рамкой: на ховере заливается целиком — так во всех девяти вхождениях кита */
.secondary.toneDanger {
  --btn-fg: var(--color-danger-text);
  --btn-border: var(--color-danger-border);
}

/* Красный текст на красном тинте всегда -strong: правило css-tokens-theming */
.tonal.toneDanger {
  --btn-bg: var(--color-danger-tint);
  --btn-fg: var(--color-danger-text-strong);
  --btn-border: var(--color-danger-border);
}

/* На фоне без подложки обычный --color-danger-text законен и совпадает с китом */
.ghost.toneDanger {
  --btn-fg: var(--color-danger-text);
}

/* Submit в сайдбаре формы и CTA внизу корзины: во всю ширину контейнера */
.block {
  display: flex;
  width: 100%;
}

/*
 * Две подписи — покоя и загрузки — лежат в одной ячейке грида. Ширина ячейки равна
 * максимуму из них, поэтому переход в загрузку и обратно не двигает ни одного пикселя.
 */
.stack {
  display: grid;
  min-width: 0;
}

.layer {
  display: inline-flex;
  grid-area: 1 / 1;
  gap: var(--space-2);
  align-items: center;
  justify-content: center;
  min-width: 0;
}

/* Место занимает, но не видна и не читается: имя кнопки собирается из активного слоя */
.layerHidden {
  visibility: hidden;
}

/*
 * visibility анимацию не останавливает: спиннер скрытого слоя крутился бы всегда,
 * а в гриде на 200 строк это 200 вечных анимаций в WebView.
 */
.layerHidden * {
  animation-play-state: paused;
}

.icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
}

/*
 * Кит ставит nowrap на все 195 кнопок. Реальная подпись длиннее: «Отправить на согласование
 * руководителю отдела закупок» в карточке 320px обязана переноситься, а артикул без пробелов —
 * рваться, иначе кнопка выдавит контейнер и появится горизонтальный скролл.
 */
.label {
  min-width: 0;
  overflow-wrap: break-word;
}

/* Кит фокус только описывает подписью «focus ring 3.5px» — здесь он нарисован */
.root:focus-visible {
  --btn-ring: var(--btn-ring-focus);

  outline: none;
}

/* Идёт запрос: заливка не меняется — белый на осветлённом градиенте кита даёт ~2.5:1 */
.loading {
  cursor: progress;
}

/*
 * Выключено выглядит одинаково у всех залитых и стеклянных вариантов: кит рисует три разных
 * погашенных фона, но «выключено» не должно зависеть от того, какой кнопка была.
 */
.root:disabled {
  --btn-bg: var(--color-neutral-tint);
  --btn-fg: var(--color-text-disabled);
  --btn-border: var(--color-border-subtle);
  --btn-shadow: var(--gloss-top);
  --btn-ring: 0 0 0 0 transparent;

  /* Выключенная кнопка перестаёт быть материалом: кит гасит её плоским тинтом,
     а размытие на неактивном узле — чистая плата за композитный слой */
  --btn-blur: none;

  cursor: default;
}

.ghost:disabled {
  --btn-bg: transparent;
  --btn-border: transparent;
  --btn-shadow: 0 0 0 0 transparent;
}

/* Ховер только на устройствах с курсором: на тач-экране он залипает */
@media (hover: hover) {
  /* Отклик стекла — не смена цвета, а уплотнение: кит уводит 0.7 в 0.92 (UI Kit 215) */
  .secondary:not(:disabled):not(.loading):hover {
    --btn-bg: var(--glass-control-hover);
    --btn-border: var(--color-accent-border);
  }

  .primary:not(:disabled):not(.loading):hover {
    --btn-bg: var(--gradient-accent-hover);
  }

  .tonal:not(:disabled):not(.loading):hover {
    --btn-bg: var(--color-accent-tint-strong);
    --btn-fg: var(--color-accent-text-hover);
  }

  .ghost:not(:disabled):not(.loading):hover {
    --btn-bg: var(--color-accent-tint-weak);
    --btn-fg: var(--color-accent-text-hover);
  }

  .primary.toneDanger:not(:disabled):not(.loading):hover {
    --btn-bg: var(--gradient-danger-hover);
  }

  /* Перед необратимым действием кнопка обязана заявить о себе: outline → solid.
     Заливка непрозрачна, стекла за ней больше нет — размытие снимается вместе с ним */
  .secondary.toneDanger:not(:disabled):not(.loading):hover {
    --btn-blur: none;
    --btn-bg: var(--gradient-danger);
    --btn-fg: var(--color-danger-text-on-solid);
    --btn-border: var(--color-danger-edge);
    --btn-shadow: var(--gloss-top-on-accent);
  }

  /*
   * Второго, более плотного красного тинта в палитре нет, а --color-danger-border (32 %)
   * как заливка даёт 4.2:1 под -strong — ниже AA. Поэтому отклик даёт рамка: тинт остаётся
   * 12-процентным (5.6:1), а контур уходит на 70 %.
   */
  .tonal.toneDanger:not(:disabled):not(.loading):hover,
  .ghost.toneDanger:not(:disabled):not(.loading):hover {
    --btn-bg: var(--color-danger-tint);
    --btn-fg: var(--color-danger-text-strong);
    --btn-border: var(--color-danger-border-focus);
  }
}

/* Нажатие: механика кита — scale(0.98) плюс утопленная тень */

.root:not(:disabled):not(.loading):active {
  --btn-shadow: var(--shadow-inset-field);

  transform: scale(var(--press-scale));
}

.secondary:not(:disabled):not(.loading):active {
  --btn-bg: var(--color-accent-tint);
  --btn-fg: var(--color-accent-text-pressed);
  --btn-border: var(--color-accent-border);
}

.primary:not(:disabled):not(.loading):active {
  --btn-bg: var(--gradient-accent-pressed);
}

.tonal:not(:disabled):not(.loading):active,
.ghost:not(:disabled):not(.loading):active {
  --btn-bg: var(--color-accent-tint-strong);
  --btn-fg: var(--color-accent-text-pressed);
}

.primary.toneDanger:not(:disabled):not(.loading):active,
.secondary.toneDanger:not(:disabled):not(.loading):active {
  --btn-blur: none;
  --btn-bg: var(--gradient-danger-pressed);
  --btn-fg: var(--color-danger-text-on-solid);
  --btn-border: var(--color-danger-edge);
}

/* На тач-экране ховера нет: нажатие обязано показать ту же подложку самостоятельно */
.tonal.toneDanger:not(:disabled):not(.loading):active,
.ghost.toneDanger:not(:disabled):not(.loading):active {
  --btn-bg: var(--color-danger-tint);
  --btn-fg: var(--color-danger-text-strong);
  --btn-border: var(--color-danger-border-focus);
}

/*
 * Тач-цель. Ступени 32 и 38 ниже нормы 44 — под пальцем добираются высотой строки.
 * any-pointer: на гибридах основной указатель мышь, но палец никуда не делся.
 * Исключения README (Chip, SegmentedControl) на кнопку не распространяются: кнопки
 * в тулбаре стоят с переносом ряда, а не неразрывной лентой.
 */
@media (any-pointer: coarse) {
  .root {
    min-height: var(--control-h-lg);
  }
}

/*
 * В режиме высокой контрастности Windows заливки, тени и кольца сбрасываются принудительно:
 * кнопка держится на рамке, а тон теряется вместе с цветом — ошибка отмечается штрихом,
 * как у Input.
 */
@media (forced-colors: active) {
  .root {
    /* Стекла в высококонтрастном режиме нет: заливку система задаёт сама, а размытие
       осталось бы поверх неё мутным пятном */
    --btn-blur: none;

    border-color: CanvasText;
  }

  .root.invalid {
    border-style: dashed;
  }

  /* box-shadow в HCM гасится спекой — кольцо фокуса заменяется системным outline */
  .root:focus-visible {
    outline: 2px solid Highlight;
    outline-offset: 2px;
  }

  .root:disabled {
    color: GrayText;
    border-color: GrayText;
  }
}

/*
 * Ошибка стоит последней в файле намеренно: ховер и нажатие вариантов идут с той же
 * специфичностью, и при любом другом порядке красная рамка под курсором становилась бы
 * синей — то есть половина сигнала об ошибке гасла ровно в момент взаимодействия.
 */
.root.invalid:not(:disabled):not(.loading) {
  --btn-ring: var(--ring-error);
  --btn-ring-focus: var(--ring-error);
  --btn-border: var(--color-danger-border-focus);
}
